import { deleteField } from "firebase/firestore";
import { firestore } from "@/services/firestore";
import { CreateEmployeeDto, Employee, EmployeeRole } from "@/types/v2";
import { Employee as EmployeeV1 } from "@/types/v1";
import { createConverter } from "@/repositories/common";
import { shop } from "@/repositories/shop";
import { user } from "@/repositories/user";
import { generic } from "@/repositories/generic";
import { DEFAULT_EMPLOYEE_COLOR, EMPLOYEE_DEFAULT_COLORS } from "@/lib/const";
import { authActions } from "@/services/auth";
import type { MergeFieldChanges } from "@/lib/employees";

const getEmployeeColor = (id: string) => {
  // Create a hash of a random string to get a consistent but unique index
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return EMPLOYEE_DEFAULT_COLORS[
    Math.abs(hash) % EMPLOYEE_DEFAULT_COLORS.length
  ];
};

export const employee = {
  get: (shopId: string, employeeId: string) => {
    return firestore.getDocument<Employee>(
      `shops/${shopId}/employees/${employeeId}`,
      createConverter<EmployeeV1, Employee>(employee)
    );
  },
  create: async (shopId: string, employeeData: CreateEmployeeDto) => {
    return generic.create<Employee>(
      {
        ...employeeData,
        schemaVersion: 2,
        color: employeeData.color || DEFAULT_EMPLOYEE_COLOR,
      },
      `shops/${shopId}/employees/`
    );
  },
  delete: async (shopId: string, employeeId: string) => {
    return generic.delete(employeeId, `shops/${shopId}/employees`, true);
  },
  softDelete: async (shopId: string, employeeId: string) => {
    const currentUser = authActions.currentUser();
    if (!currentUser) {
      throw new Error("No current user found for soft delete");
    }
    return employee.update(shopId, employeeId, {
      _deleted: true,
      _deletedBy: currentUser.uid,
    });
  },
  /**
   * Undoes `softDelete`. `_deleted` is set to `false` rather than removed:
   * functions-v2 queries employees with `_deleted == false`, which a missing
   * field would not match. Auth account and role are deliberately untouched;
   * the employee comes back inactive and is re-activated separately.
   */
  restore: async (shopId: string, employeeId: string) => {
    return firestore.updateDocument<Record<string, unknown>>(
      `shops/${shopId}/employees/${employeeId}`,
      { _deleted: false, _deletedBy: deleteField() }
    );
  },
  /**
   * Merges a manually-created employee into a synced one in a single atomic
   * batch: the synced employee receives `changes` and is brought back
   * (`_deleted: false`, `_deletedBy` removed), and the manual employee is
   * soft-deleted. Both writes commit or neither does. `_deleted` is set to
   * `false` rather than removed for the same reason as `restore`: functions-v2
   * queries employees with `_deleted == false`, which a missing field would
   * not match.
   *
   * When the manual employee is already deleted it is left as is, so the
   * original `_deletedBy` isn't overwritten.
   */
  mergeManualIntoSynced: async (
    shopId: string,
    manualId: string,
    syncedId: string,
    changes: MergeFieldChanges,
    options: { manualAlreadyDeleted?: boolean } = {}
  ): Promise<void> => {
    const currentUser = authActions.currentUser();
    if (!currentUser) {
      throw new Error("No current user found for merge");
    }
    await firestore.batchWrite([
      {
        type: "update",
        path: `shops/${shopId}/employees/${syncedId}`,
        data: { ...changes, _deleted: false, _deletedBy: deleteField() },
      },
      ...(options.manualAlreadyDeleted
        ? []
        : [
            {
              type: "update" as const,
              path: `shops/${shopId}/employees/${manualId}`,
              data: { _deleted: true, _deletedBy: currentUser.uid },
            },
          ]),
    ]);
  },
  isV1: (employee: EmployeeV1 | Employee): employee is EmployeeV1 => {
    return (employee as Employee).schemaVersion === undefined;
  },
  transformV1ToV2: (employee: EmployeeV1): Employee => {
    const employeeId = `${employee.id}`; // Convert to string
    return {
      ...employee,
      id: employeeId,
      sourceLastUpdated: new Date(0),
      schemaVersion: 2,
      color: getEmployeeColor(employeeId),
    };
  },
  subscribeAll: (
    id: string,
    callback: (doc: Employee[] | undefined) => void
  ) => {
    return firestore.subscribeCollection<Employee>(
      `shops/${id}/employees`,
      callback,
      createConverter<EmployeeV1, Employee>(employee)
    );
  },
  update: async (
    shopId: string,
    employeeId: string,
    employeeData: Partial<Employee>
  ) => {
    return firestore.updateDocument<Partial<Employee>>(
      `shops/${shopId}/employees/${employeeId}`,
      { ...employeeData }
    );
  },

  toggleVisibility: async (shopId: string, id: string, hidden: boolean) => {
    return employee.update(shopId, id, { hideInShopHero: hidden });
  },
  updateEmployeeRole: async (
    shopId: string,
    employeeId: string,
    role?: EmployeeRole
  ) => {
    const authUser = await removeExistingRoles(shopId, employeeId);

    if (!authUser) {
      return;
    }

    // If employee role is undefined, then we are good. Nothing else to do, except update the employee

    if (role) {
      await user.updateUserRole({
        userId: authUser.data.uid,
        userRole: role,
        userRoleEnabled: true,
        userShopId: shopId,
      });
    }
    return employee.update(shopId, employeeId, {
      shopHeroRole: role ?? null,
      shopHeroAuthUid: authUser.data.uid,
    });
  },
};

const removeExistingRoles = async (shopId: string, employeeId: string) => {
  const employeeData = await employee.get(shopId, employeeId);
  const shopData = await shop.get(shopId);
  if (!shopData || !employeeData) return;
  const authUser = await user.getId(employeeData.email);
  if (!authUser.data) return;
  const currentRoles: EmployeeRole[] = [];
  if (shopData.adminUserIds?.includes(authUser.data.uid)) {
    currentRoles.push("admin");
  }
  if (shopData.technicianUserIds?.includes(authUser.data.uid)) {
    currentRoles.push("technician");
  }
  if (shopData.officeUserIds?.includes(authUser.data.uid)) {
    currentRoles.push("office");
  }
  if (shopData.viewOnlyUserIds?.includes(authUser.data.uid)) {
    currentRoles.push("viewOnly");
  }
  for (const role of currentRoles) {
    await user.updateUserRole({
      userId: authUser.data.uid,
      userRole: role,
      userRoleEnabled: false,
      userShopId: shopId,
    });
  }
  return authUser;
};
